import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { useFormik } from 'formik';
import { deburr, get } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { createValidationSchema } from 'src/schemas/editProductDetails.schema';
import { round } from 'src/utils/number';

export interface ProductData {
  title: string;
  price: string;
  description: string;
  imageUrl: string;
  productDetails: Record<string, string>;
  manufacturingAndPackaging: Record<string, string>;
}

export interface FormData {
  itemName: string;
  price: string;
  mfgName: string;
  manufacturerNumber: string;
  brandName: string;
  uom: string;
  casePackUnits: string;
  weight: string;
  upc: string;
  unspsc: string;
}

interface UseEditItemInformationFormProps {
  productData: ProductData;
  isOpen: boolean;
}

interface UseEditItemInformationFormResult {
  formik: ReturnType<typeof useFormik<FormData>>;
  uomOptions: Array<{ text: string; value: string }>;
  selectedUOM: { text: string; value: string };
  uomFilterValue: string;
  handleUOMChange: (event: DropDownListChangeEvent) => void;
  handleUOMFilterChange: (event: any) => void;
  handleUOMBlur: (event: any) => void;
  handleUOMOpen: () => void;
  commitPendingUomFilter: () => Promise<string>;
  getSaveButtonDisabledReason: (isSaving: boolean) => string | null;
}

const UOM_OPTIONS = ['CS', 'EA', 'BX', 'PK', 'CA', 'GL', 'LB', 'OZ', 'PT', 'QT', 'GA'];

const useEditItemInformationForm = ({
  productData,
  isOpen,
}: UseEditItemInformationFormProps): UseEditItemInformationFormResult => {
  const initialValues: FormData = useMemo(
    () => ({
      itemName: deburr(get(productData, 'title', '')).trim(),
      price: productData.price?.replace(/[^0-9.]/g, '') || '',
      mfgName: productData.manufacturingAndPackaging['Mfg Name'] || '',
      manufacturerNumber: productData.manufacturingAndPackaging['Mfg #'] || '',
      brandName: productData.manufacturingAndPackaging['Brand Name'] || '',
      uom: productData.manufacturingAndPackaging.UOM || '',
      casePackUnits: productData.manufacturingAndPackaging['Case Pack Units'] || '',
      weight: productData.manufacturingAndPackaging.Weight || '',
      upc: productData.productDetails.UPC || '',
      unspsc: productData.productDetails.UNSPSC || '',
    }),
    [productData]
  );

  const validationSchema = useMemo(
    () => createValidationSchema(initialValues.price, initialValues.uom),
    [initialValues.price, initialValues.uom]
  );

  const formik = useFormik<FormData>({
    initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: () => {
      // Handled in parent component
    },
  });

  const { resetForm } = formik;

  // State to track custom UOMs typed during the session
  const [customUOMs, setCustomUOMs] = useState<string[]>([]);
  // State to track filter input value for clearing placeholder
  const [uomFilterValue, setUomFilterValue] = useState<string>('');

  // Reset form and initialize custom UOM when dialog opens
  useEffect(() => {
    if (isOpen) {
      resetForm({ values: initialValues });
      setUomFilterValue('');

      // If initial UOM from database is custom (not in predefined list), preserve it
      const initialUom = initialValues.uom?.trim();
      if (initialUom && !UOM_OPTIONS.includes(initialUom)) {
        setCustomUOMs([initialUom]);
      } else {
        setCustomUOMs([]);
      }
    }
  }, [isOpen, initialValues, resetForm]);

  // Base options: predefined UOMs + any saved custom UOM (selected previously)
  const baseUomOptions = useMemo(() => {
    const existingOptions = UOM_OPTIONS.map((uom) => ({ text: uom, value: uom }));

    // Add saved custom UOM if exists (this persists after selection)
    const customUOMOptions =
      customUOMs.length > 0 && !UOM_OPTIONS.includes(customUOMs[0])
        ? [{ text: customUOMs[0], value: customUOMs[0] }]
        : [];

    return [...customUOMOptions, ...existingOptions];
  }, [customUOMs]);

  // Display options: keep the full picklist available, and offer the typed value as a
  // custom choice whenever it is not already an exact match. That lets a user recover
  // from a too-long custom UOM by picking EA/CS/etc., or by shortening the typed text.
  const uomOptions = useMemo(() => {
    const typed = uomFilterValue.trim();
    const filterValue = typed.toLowerCase();

    if (!filterValue) {
      return baseUomOptions;
    }

    const exactMatchExists = baseUomOptions.some((opt) => opt.text.toLowerCase() === filterValue);
    if (exactMatchExists) {
      const isPredefined = UOM_OPTIONS.some((uom) => uom.toLowerCase() === filterValue);
      if (isPredefined) {
        return baseUomOptions.filter((opt) => opt.text.toLowerCase().includes(filterValue));
      }
      return baseUomOptions;
    }

    return [{ text: typed, value: typed }, ...baseUomOptions];
  }, [baseUomOptions, uomFilterValue]);

  const applyUomValue = (value: string) => {
    const isLb = value.toLowerCase() === 'lb';
    formik.setFieldValue('price', round(Number(formik.values.price || '0'), isLb ? 3 : 2), true);
    formik.setFieldTouched('price', true, true);

    const trimmed = value.trim();
    if (trimmed && !UOM_OPTIONS.includes(trimmed)) {
      setCustomUOMs([trimmed]);
    }

    formik.setFieldTouched('uom', true, false);
    return formik.setFieldValue('uom', value, true);
  };

  const handleUOMChange = (event: DropDownListChangeEvent) => {
    const value = event.value?.text || event.value?.value || event.value || '';
    setUomFilterValue('');
    void applyUomValue(value);
  };

  const handleUOMFilterChange = (event: any) => {
    const filterValue = event.filter?.value || '';
    setUomFilterValue(filterValue);

    // After a max-length error, typing in the filter is editing the UOM itself so the
    // user can shorten it without first clicking another picklist item.
    if (formik.errors.uom || formik.values.uom.length > 50) {
      formik.setFieldTouched('uom', true, false);
      void formik.setFieldValue('uom', filterValue, true);
    }
  };

  const handleUOMOpen = () => {
    if (formik.errors.uom && formik.values.uom) {
      setUomFilterValue(formik.values.uom);
    }
  };

  const handleUOMBlur = (event: any) => {
    const typed = uomFilterValue.trim();
    if (typed && formik.errors.uom) {
      void applyUomValue(typed);
    } else {
      formik.handleBlur(event);
    }
    setUomFilterValue('');
  };

  const commitPendingUomFilter = async (): Promise<string> => {
    const typed = uomFilterValue.trim();
    const nextUom = typed || formik.values.uom;
    if (typed) {
      await applyUomValue(typed);
      setUomFilterValue('');
    } else {
      formik.setFieldTouched('uom', true, false);
    }
    return nextUom;
  };

  const selectedUOM = useMemo(() => {
    const found = uomOptions.find((opt) => opt.value === formik.values.uom);
    if (found) return found;
    if (formik.values.uom) {
      return { text: formik.values.uom, value: formik.values.uom };
    }
    return { text: '', value: '' };
  }, [uomOptions, formik.values.uom]);

  const getSaveButtonDisabledReason = (isSaving: boolean): string | null => {
    if (isSaving) {
      return 'Saving changes...';
    }
    const validationMessages = Object.values(formik.errors).filter((error) => error);
    if (validationMessages.length > 0) {
      return null;
    }
    const hasFormChanges = JSON.stringify(formik.values) !== JSON.stringify(initialValues);
    if (!hasFormChanges) {
      return 'No data changes';
    }
    return null;
  };

  return {
    formik,
    uomOptions,
    selectedUOM,
    uomFilterValue,
    handleUOMChange,
    handleUOMFilterChange,
    handleUOMBlur,
    handleUOMOpen,
    commitPendingUomFilter,
    getSaveButtonDisabledReason,
  };
};

export default useEditItemInformationForm;
